/* Native purchases, a single flat delivery charge, and a location-only map. */
(() => {
  'use strict';
  const all = (selector, base = document) => Array.from(base.querySelectorAll(selector));
  const one = (selector, base = document) => base.querySelector(selector);
  const core = window.VoltlinkDelivery;
  const root = window.Shopify?.routes?.root || '/';
  let store = {};
  try { store = JSON.parse(one('#vl-store-data')?.textContent || '{}'); } catch (_) {}
  const currency = store.currency || 'GBP';
  const money = cents => new Intl.NumberFormat('en-GB', {style: 'currency', currency}).format(cents / 100);
  const records = new Map((store.products || []).map(product => [product.handle, product]));
  if (store.current) records.set(store.current.handle, store.current);
  function purchaseForm(record) {
    if (!record || !/^\d+$/.test(String(record.variantId)) || !(Number(record.variantPrice) > 0)) return null;
    const form = document.createElement('form');
    form.className = 'volt-buy-form vl-restored-purchase'; form.method = 'post'; form.action = root + 'cart/add';
    for (const [name, value] of Object.entries({id: record.variantId, quantity: '1', return_to: root + 'cart'})) {
      const input = document.createElement('input'); input.type = 'hidden'; input.name = name; input.value = value; form.append(input);
    }
    const button = document.createElement('button'); button.type = 'submit'; button.className = 'btn btn-primary';
    button.disabled = !(record.variantAvailable ?? record.available);
    button.textContent = button.disabled ? 'Currently unavailable' : 'Buy charger only →';
    button.setAttribute('aria-label', button.disabled ? record.title + ' is unavailable' : 'Buy ' + record.title + ' — charger only');
    form.append(button); return form;
  }
  function initPurchases(base = document) {
    all('[data-vl-card]', base).forEach(card => {
      const record = card._vlModel?.record || records.get(card.dataset.vlHandle);
      const compare = one('[data-compare]', card);
      if (compare && Number(record?.variantPrice) > 0) compare.dataset.price = money(Number(record.variantPrice));
      if (one('.vl-restored-purchase', card)) return;
      const form = purchaseForm(record); if (!form) return;
      const details = one('[data-vl-details]', card);
      if (details) { details.before(form); details.classList.remove('btn-dark'); details.classList.add('btn-outline'); }
      else one('.volt-gallery-content', card)?.append(form);
    });
    all('.vl-scroller-toggle', base).forEach(button => button.remove());
    all('.volt-brand-ribbon', base).forEach(ribbon => {
      ribbon.removeAttribute('data-paused'); ribbon.tabIndex = 0;
      ribbon.setAttribute('aria-label', 'Manufacturer logos. Focus to pause scrolling.');
    });
  }
  initPurchases();
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-vl-photo],[data-vl-details],[data-vl-product-stage]');
    if (!trigger) return;
    const card = trigger.closest('[data-vl-card]');
    const record = card?._vlModel?.record || records.get(card?.dataset.vlHandle) || records.get(trigger.closest('[data-vl-product]')?.dataset.vlProduct);
    const actions = one('.vl-modal-actions'); if (!actions) return;
    all('.vl-restored-purchase', actions).forEach(form => form.remove());
    const form = purchaseForm(record); if (form) actions.prepend(form);
  });
  function initDelivery(base = document) {
    if (!core) return;
    all('[data-vl-flat-delivery]', base).forEach(box => {
      if (box.dataset.initialised) return; box.dataset.initialised = 'true';
      const total = one('[data-vl-delivered-total]', box), totalRow = one('[data-vl-total-row]', box);
      const status = one('[data-vl-delivery-status]', box);
      let cartDirty = false;
      const update = () => {
        if (!total || !totalRow) return;
        const quantity = box.dataset.mode === 'product' ? Number(one('.product-buy-form [name="quantity"]', box.closest('[data-vl-product]') || document)?.value ?? 1) : 1;
        const value = core.deliveredTotal(Number(box.dataset.subtotal), quantity);
        totalRow.hidden = value === null || cartDirty || box.dataset.currency !== 'GBP';
        if (!totalRow.hidden) total.textContent = money(value);
      };
      if (box.dataset.mode === 'product') {
        one('.product-buy-form [name="quantity"]', box.closest('[data-vl-product]') || document)?.addEventListener('input', update);
      } else {
        all('input[name="updates[]"]', box.closest('form') || document).forEach(input => input.addEventListener('input', () => {
          cartDirty = true; update();
          if (status) { status.hidden = false; status.textContent = 'Update your bag to refresh the total. Standard delivery stays £19.95 per order.'; }
        }));
      }
      update();
    });
  }
  initDelivery();
  let leafletLoading;
  function loadLeaflet(url) {
    if (window.L) return Promise.resolve(window.L);
    if (!leafletLoading) leafletLoading = new Promise((resolve, reject) => {
      const script = document.createElement('script'); script.src = url; script.async = true;
      const timeout = setTimeout(() => reject(new Error('Map load timed out')), 15000);
      script.onload = () => { clearTimeout(timeout); window.L ? resolve(window.L) : reject(new Error('Map unavailable')); };
      script.onerror = () => { clearTimeout(timeout); reject(new Error('Map unavailable')); };
      document.head.append(script);
    });
    return leafletLoading;
  }
  async function drawMap(section) {
    if (!core || section.dataset.mapInitialised) return; section.dataset.mapInitialised = 'true';
    const canvas = one('[data-vl-map]', section), loading = one('[data-vl-map-status]', section);
    if (!canvas) return;
    try {
      const L = await loadLeaflet(section.dataset.leafletSrc);
      if (!section.isConnected) return;
      const centre = [core.base.latitude, core.base.longitude];
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const map = L.map(canvas, {scrollWheelZoom: false, zoomControl: true, attributionControl: true, zoomAnimation: !reduced, fadeAnimation: !reduced}).setView(centre, 13);
      section._vlMap = map;
      const tiles = L.tileLayer(section.dataset.tileUrl || 'https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19, attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(map);
      const icon = L.divIcon({className: 'vl-location-pin', html: '<span aria-hidden="true"></span>', iconSize: [28, 36], iconAnchor: [14, 36], tooltipAnchor: [0, -38]});
      L.marker(centre, {icon, title: 'VOLTLINK · Newport', alt: 'VOLTLINK location in Newport', keyboard: true})
        .addTo(map).bindTooltip('VOLTLINK · Newport', {permanent: true, direction: 'top', offset: [0, 0]});
      if (loading) loading.hidden = true;
      tiles.on('tileerror', () => {
        if (loading) { loading.hidden = false; loading.textContent = 'Some map tiles could not load. Open our location using the link below.'; }
      });
      if ('ResizeObserver' in window) {
        section._vlMapResize = new ResizeObserver(() => map.invalidateSize({pan: false}));
        section._vlMapResize.observe(canvas);
      }
    } catch (_) {
      if (loading) { loading.hidden = false; loading.textContent = 'The map could not load. Open our location using the link below.'; }
    }
  }
  function initMaps(base = document) {
    all('[data-vl-coverage]', base).forEach(section => {
      if (section.dataset.mapObserved) return; section.dataset.mapObserved = 'true';
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) { observer.disconnect(); drawMap(section); }
        });
        section._vlMapObserver = observer; observer.observe(section);
      } else drawMap(section);
    });
  }
  initMaps();
  document.addEventListener('shopify:section:load', event => { initPurchases(event.target); initDelivery(event.target); initMaps(event.target); });
  document.addEventListener('shopify:section:unload', event => {
    all('[data-vl-coverage]', event.target).forEach(section => {
      section._vlMapObserver?.disconnect(); section._vlMapResize?.disconnect(); section._vlMap?.remove();
    });
  });
})();
